import { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../context';
import api from '../../hooks/useApi';
import Modal from '../shared/Modal';
import FeatureTour from '../shared/FeatureTour';
import useFeatureTour from '../../hooks/useFeatureTour';
import { formatDate } from '../../utils/format';
import { SLT_REGISTER_TOUR_ID, sltRegisterTourSteps } from './sltRegisterTour';
import { RiskMatrix } from './RiskRegisterPage';
import { matchesSltFilters, sltMatrixRisks } from '../../utils/sltScope';

// The SLT Register: risks, issues and actions for the Senior Leadership Team
// only (src/api/routes/sltRegister.js). The server decides everything: which
// items this person sees, whether they hold the HR tier (can_hr) and who may
// own an item. The page only draws what it is given.

const TYPES = [
  { value: 'risk',   label: 'Risks',   singular: 'Risk',   cls: 'bg-red-50 text-red-700' },
  { value: 'issue',  label: 'Issues',  singular: 'Issue',  cls: 'bg-amber-50 text-amber-700' },
  { value: 'action', label: 'Actions', singular: 'Action', cls: 'bg-blue-50 text-blue-700' },
];
const typeOf = (v) => TYPES.find(t => t.value === v) || TYPES[0];
const RATINGS = [1, 2, 3, 4, 5];
const scoreCls = (s) => (s >= 15 ? 'bg-red-100 text-red-700' : s >= 8 ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-600');
const HrBadge = () => <span className="ml-1 px-1.5 py-0.5 rounded text-[8px] font-bold bg-red-50 text-red-700">HR</span>;

function ItemModal({ initial, parent, items, owners, canHr, onClose, onSaved, onAddAction }) {
  const editing = !!initial?.id;
  const [form, setForm] = useState(() => ({
    item_type: parent ? 'action' : 'risk', title: '', description: '', owner_id: '', due_date: '',
    probability: 3, impact: 3, hr_confidential: !!parent?.hr_confidential, closure_notes: '',
    ...(initial || {}),
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const t = typeOf(form.item_type);
  const scored = form.item_type !== 'action';
  const hr = !!form.hr_confidential;
  const parentIsHr = !!(parent?.hr_confidential || items.find(i => i.id === initial?.parent_id)?.hr_confidential);
  const ownerChoices = owners.filter(o => !hr || o.can_hr);
  const children = editing ? items.filter(i => i.parent_id === initial.id) : [];
  // Ticking HR drops an owner who could not see the item, and says so here,
  // rather than leaving a choice the server will refuse.
  const [hrNote, setHrNote] = useState('');
  const setHr = (on) => {
    const owner = owners.find(o => o.id === form.owner_id);
    if (on && owner && !owner.can_hr) {
      setForm(p => ({ ...p, hr_confidential: true, owner_id: '' }));
      setHrNote(`${owner.name} cannot see HR items, so the owner has been cleared.`);
    } else { set('hr_confidential', on); setHrNote(''); }
  };

  const save = async (extra = {}) => {
    if (!form.title.trim()) { setError('Give it a title.'); return; }
    if (form.owner_id && !ownerChoices.some(o => o.id === form.owner_id)) { setError('That owner cannot see HR confidential items. Choose another owner.'); return; }
    setSaving(true); setError('');
    const body = {
      title: form.title, description: form.description, owner_id: form.owner_id || null, due_date: form.due_date || null,
      probability: scored ? Number(form.probability) : null, impact: scored ? Number(form.impact) : null,
      ...(canHr ? { hr_confidential: hr } : {}),
      ...(editing ? { closure_notes: form.closure_notes } : { item_type: form.item_type, parent_id: parent?.id || null }),
      ...extra,
    };
    try {
      await (editing ? api.put(`slt-register/${initial.id}`, body) : api.post('slt-register', body));
      onSaved();
    } catch (e) { setError(e?.data?.error || e?.message || 'Save failed'); setSaving(false); }
  };

  const closeLabel = form.item_type === 'action' ? 'Mark complete' : `Close ${t.singular.toLowerCase()}`;
  return (
    <Modal
      title={editing ? `${initial.reference}: ${t.singular}` : parent ? `New action against ${parent.reference}` : 'New item'}
      onClose={onClose}
      wide
      footer={
        <>
          {error && <span className="mr-auto text-[9px] text-red-600">{error}</span>}
          <button onClick={onClose} className="px-3 py-1.5 text-[9px] border border-gray-200 rounded hover:bg-gray-50">Cancel</button>
          {editing && initial.status === 'open' && (
            <button onClick={() => save({ status: 'closed' })} disabled={saving} className="px-3 py-1.5 text-[9px] font-semibold border border-navy text-navy rounded hover:bg-gray-50 disabled:opacity-50">{closeLabel}</button>
          )}
          {editing && initial.status === 'closed' && (
            <button onClick={() => save({ status: 'open' })} disabled={saving} className="px-3 py-1.5 text-[9px] font-semibold border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50">Reopen</button>
          )}
          <button onClick={() => save()} disabled={saving} className="px-4 py-1.5 text-[9px] font-bold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-50">
            {saving ? 'Saving…' : editing ? 'Save changes' : `Raise ${t.singular.toLowerCase()}`}
          </button>
        </>
      }
    >
      <div className="space-y-3 text-[10px]">
        {!editing && !parent && (
          <div className="flex gap-1">
            {TYPES.map(x => (
              <button key={x.value} onClick={() => set('item_type', x.value)}
                className={`px-3 py-1 rounded-full border text-[9px] font-semibold ${form.item_type === x.value ? 'bg-navy text-white border-navy' : 'bg-white text-gray-500 border-gray-200'}`}>
                {x.singular}
              </button>
            ))}
          </div>
        )}
        <label className="block">
          <span className="text-[8px] font-bold uppercase text-gray-400">Title</span>
          <input value={form.title} onChange={e => set('title', e.target.value)} className="mt-0.5 w-full border border-gray-200 rounded px-2 py-1.5 bg-white" autoFocus />
        </label>
        <label className="block">
          <span className="text-[8px] font-bold uppercase text-gray-400">Detail</span>
          <textarea value={form.description || ''} onChange={e => set('description', e.target.value)} rows={4} className="mt-0.5 w-full border border-gray-200 rounded px-2 py-1.5 bg-white" />
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <label className="block col-span-2">
            <span className="text-[8px] font-bold uppercase text-gray-400">Owner</span>
            <select value={form.owner_id || ''} onChange={e => set('owner_id', e.target.value)} className="mt-0.5 w-full border border-gray-200 rounded px-2 py-1.5 bg-white">
              <option value="">No owner yet</option>
              {ownerChoices.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[8px] font-bold uppercase text-gray-400">{form.item_type === 'action' ? 'Due' : 'Review by'}</span>
            <input type="date" value={form.due_date || ''} onChange={e => set('due_date', e.target.value)} className="mt-0.5 w-full border border-gray-200 rounded px-2 py-1.5 bg-white" />
          </label>
        </div>
        {scored && (
          <div className="flex items-end gap-2">
            {[['probability', 'Probability'], ['impact', 'Impact']].map(([k, lbl]) => (
              <label key={k} className="block">
                <span className="text-[8px] font-bold uppercase text-gray-400">{lbl}</span>
                <select value={form[k] || 3} onChange={e => set(k, Number(e.target.value))} className="mt-0.5 border border-gray-200 rounded px-2 py-1.5 bg-white">
                  {RATINGS.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
            ))}
            <span className={`px-2 py-1.5 rounded font-bold ${scoreCls(form.probability * form.impact)}`}>Score {form.probability * form.impact}</span>
          </div>
        )}
        {canHr && (
          <label className="flex items-start gap-2 p-2 rounded border border-red-200 bg-red-50">
            <input type="checkbox" checked={hr} disabled={parentIsHr} onChange={e => setHr(e.target.checked)} className="mt-0.5" />
            <span className="text-red-700">
              <span className="font-bold">HR confidential.</span> Only people on the HR tier will see this{form.item_type !== 'action' ? ' and any actions raised against it' : ''}.
              {parentIsHr && ' It sits under an HR item, so it stays HR.'}
              {hrNote && <span className="block mt-1 font-semibold">{hrNote}</span>}
            </span>
          </label>
        )}
        {editing && (
          <label className="block">
            <span className="text-[8px] font-bold uppercase text-gray-400">{form.item_type === 'action' ? 'Completion notes' : 'Closure notes'}</span>
            <textarea value={form.closure_notes || ''} onChange={e => set('closure_notes', e.target.value)} rows={2} className="mt-0.5 w-full border border-gray-200 rounded px-2 py-1.5 bg-white" />
          </label>
        )}
        {editing && form.item_type !== 'action' && (
          <div className="border-t border-gray-200 pt-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[8px] font-bold uppercase text-gray-400">Actions</span>
              <button onClick={() => onAddAction(initial)} className="text-[9px] text-gold hover:underline">Add action</button>
            </div>
            {children.length === 0 && <div className="text-gray-400">No actions raised against this yet.</div>}
            {children.map(c => (
              <div key={c.id} className={`flex gap-2 py-0.5 ${c.status === 'closed' ? 'text-gray-400 line-through' : 'text-navy'}`}>
                <span className="font-mono">{c.reference}</span><span className="flex-1">{c.title}</span>
                <span>{c.owner_name || 'No owner'}</span><span>{c.due_date ? formatDate(c.due_date) : ''}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

export default function SltRegisterPage() {
  const { currentUser: user } = useApp();
  const [data, setData] = useState({ items: [], can_hr: false, owners: [] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [type, setType] = useState('all');
  const [showClosed, setShowClosed] = useState(false);
  const [mine, setMine] = useState(false);
  const [hrOnly, setHrOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // { initial?, parent? }

  const load = useCallback(() => {
    api.get('slt-register')
      .then(d => { setData(d); setLoadError(''); })
      .catch(e => setLoadError(e?.data?.error || e?.message || 'Could not load the register'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  const tour = useFeatureTour(SLT_REGISTER_TOUR_ID, { enabled: !loading && !loadError });

  const filters = { search, mine, userId: user?.id, hrOnly };
  const shown = data.items.filter(i =>
    (type === 'all' || i.item_type === type) &&
    (showClosed || i.status === 'open') &&
    matchesSltFilters(i, filters));
  const openOf = (t) => data.items.filter(i => i.status === 'open' && (t === 'all' || i.item_type === t)).length;
  const today = new Date().toISOString().slice(0, 10);

  if (loading) return <div className="text-center py-8 text-[10px] text-gray-400">Loading…</div>;
  if (loadError) return <div className="text-center py-8 text-[10px] text-red-600">{loadError}</div>;

  return (
    <div className="fade-in">
      <div data-tour="page-header" className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-base font-extrabold text-navy">SLT Register</h2>
          <div className="text-[9px] text-gray-400 mt-0.5">Risks, issues and actions for the Senior Leadership Team. Nobody else can see this page.</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={tour.start} className="w-[23px] h-[23px] rounded-full border border-gray-200 text-gray-500 hover:text-navy hover:border-navy text-[11px] font-bold" aria-label="Replay the guided tour of this page" title="Replay the guided tour of this page">?</button>
          <button data-tour="new-item" onClick={() => setEditing({})} className="px-3 py-1.5 text-[9px] font-bold bg-navy text-white rounded hover:bg-navy/80">+ New item</button>
        </div>
      </div>

      <div data-tour="filters" className="flex flex-wrap items-center gap-2 mb-3">
        {[{ value: 'all', label: 'All' }, ...TYPES].map(t => (
          <button key={t.value} onClick={() => setType(t.value)}
            className={`px-3 py-1.5 text-[9px] font-semibold rounded-full border ${type === t.value ? 'bg-navy text-white border-navy' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'}`}>
            {t.label} ({openOf(t.value)} open)
          </button>
        ))}
        <label className="flex items-center gap-1 text-[9px] text-gray-600"><input type="checkbox" checked={mine} onChange={e => setMine(e.target.checked)} />Mine</label>
        <label className="flex items-center gap-1 text-[9px] text-gray-600"><input type="checkbox" checked={showClosed} onChange={e => setShowClosed(e.target.checked)} />Show closed</label>
        {data.can_hr && (
          <label data-tour="hr-filter" className="flex items-center gap-1 text-[9px] text-red-700"><input type="checkbox" checked={hrOnly} onChange={e => setHrOnly(e.target.checked)} />HR confidential only</label>
        )}
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search the register" aria-label="Search the register"
          className="ml-auto border border-gray-200 rounded px-2 py-1 text-[10px] bg-white w-48" />
      </div>

      {(type === 'all' || type === 'risk') && (
        <div data-tour="matrix" className="mb-3">
          <RiskMatrix risks={sltMatrixRisks(data.items, filters)} />
        </div>
      )}

      <div data-tour="table" className="bg-white border border-gray-200 rounded overflow-x-auto">
        <table className="w-full text-[10px]">
          <thead className="bg-gray-50 text-[8px] uppercase text-gray-400">
            <tr>
              <th className="text-left px-2 py-1.5">Ref</th><th className="text-left px-2 py-1.5">Type</th>
              <th className="text-left px-2 py-1.5">Title</th><th className="text-left px-2 py-1.5">Owner</th>
              <th className="text-left px-2 py-1.5">Due</th><th className="text-left px-2 py-1.5">Score</th>
              <th className="text-left px-2 py-1.5">Status</th>
            </tr>
          </thead>
          <tbody>
            {shown.map(i => {
              const t = typeOf(i.item_type);
              const overdue = i.status === 'open' && i.due_date && i.due_date < today;
              return (
                <tr key={i.id} onClick={() => setEditing({ initial: i })} className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer">
                  <td className="px-2 py-1.5 font-mono text-navy">{i.reference}</td>
                  <td className="px-2 py-1.5"><span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${t.cls}`}>{t.singular}</span></td>
                  <td className="px-2 py-1.5 text-navy">
                    {i.title}{!!i.hr_confidential && <HrBadge />}
                    {i.parent_reference && <span className="ml-1 text-[8px] text-gray-400">against {i.parent_reference}</span>}
                  </td>
                  <td className="px-2 py-1.5 text-gray-600">{i.owner_name || '-'}</td>
                  <td className={`px-2 py-1.5 ${overdue ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>{i.due_date ? formatDate(i.due_date) : '-'}</td>
                  <td className="px-2 py-1.5">{i.risk_score ? <span className={`px-1.5 py-0.5 rounded font-bold ${scoreCls(i.risk_score)}`}>{i.risk_score}</span> : '-'}</td>
                  <td className="px-2 py-1.5 text-gray-600">{i.status === 'closed' ? (i.item_type === 'action' ? 'Complete' : 'Closed') : 'Open'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {shown.length === 0 && (
          <div className="text-center py-6 text-[10px] text-gray-400">
            {data.items.length === 0 ? 'Nothing raised yet.' : 'Nothing matches these filters.'}
          </div>
        )}
      </div>

      {editing && (
        <ItemModal
          key={editing.initial ? `edit-${editing.initial.id}` : editing.parent ? `action-under-${editing.parent.id}` : 'new'}
          initial={editing.initial} parent={editing.parent}
          items={data.items} owners={data.owners} canHr={data.can_hr}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          onAddAction={(parentItem) => setEditing({ parent: parentItem })}
        />
      )}
      {tour.open && <FeatureTour steps={sltRegisterTourSteps} context={{ canHr: data.can_hr }} onFinish={tour.finish} onSkip={tour.skip} />}
    </div>
  );
}
